import type { UseCaseType } from '../types/content-scoring.types';
import type { ConnectorCode } from '../types/scheduled-enrichment.types';
import { COUNTRY_FILTER_KEYS, LANGUAGE_FILTER_KEYS } from '../utils/default-selection-filters';

/**
 * Per-platform descriptor for the staged scheduled-enrichment funnel. The flow
 * (content-scoring → sample → full-catalog → launch) is identical across
 * platforms; only these literals differ. Consumed by the core adapter,
 * product-selection page, delivery page, and batch-resume hook so a single
 * implementation drives both the Meta Ads and Google Ads flows.
 */
export interface ScheduledEnrichmentPlatform {
  /** UI platform key — matches `ExperimentPlatform` values. */
  platform: 'meta' | 'google';
  /** Connector code sent on every job/trigger request body. */
  connectorCode: ConnectorCode;
  /** Use-case discriminator sent on every job/trigger request body. */
  useCase: UseCaseType;
  /** `useConnectedConnectors().code` used to resolve the catalog connection. */
  connectionCode: 'META_COMMERCE' | 'GOOGLE_MERCHANT';
  /** Route prefix for the funnel — e.g. `/enrichments/meta`. */
  routeBase: '/enrichments/meta' | '/enrichments/google';
  /** Product-selection entry route (`routeBase` + `/product-selection`). */
  productSelectionRoute: string;
  /** Content-enrichment funnel base (`routeBase` + `/content-enrichment`). */
  contentEnrichmentBase: string;
  /** i18n key prefix for the product-selection page copy (branding differs per platform). */
  i18nKeyPrefix: 'metaProductSelection' | 'googleProductSelection';
  /**
   * Product-scope filters the backend requires before it will trigger a run — each
   * entry lists the keys/columns that filter may be exposed under. Mirrors the
   * backend's per-connector rule so Proceed is blocked up front instead of a 400.
   */
  requiredFilters: readonly (readonly string[])[];
}

/** Meta Ads scheduled-enrichment platform config. */
export const META_PLATFORM: ScheduledEnrichmentPlatform = {
  platform: 'meta',
  connectorCode: 'META',
  useCase: 'META_ADS',
  connectionCode: 'META_COMMERCE',
  routeBase: '/enrichments/meta',
  productSelectionRoute: '/enrichments/meta/product-selection',
  contentEnrichmentBase: '/enrichments/meta/content-enrichment',
  i18nKeyPrefix: 'metaProductSelection',
  // Backend: "Missing required product scope filters for META_COMMERCE: availability".
  requiredFilters: [['availability']],
};

/**
 * Human-facing platform name for progress/loading copy (brand noun, not translated).
 *
 * Only an explicit `meta` yields "Meta". It used to be the other way round —
 * anything that wasn't `google` read as Meta — so a caller that failed to supply
 * the platform silently branded a Google run as Meta. Google is the safer
 * fallback: the experiment funnel it also covers is GMC.
 */
export function scheduledPlatformLabel(platform: 'meta' | 'google' | null | undefined): string {
  return platform === 'meta' ? 'Meta' : 'Google';
}

/** Google Ads (GMC) scheduled-enrichment platform config. */
export const GOOGLE_PLATFORM: ScheduledEnrichmentPlatform = {
  platform: 'google',
  connectorCode: 'GMC',
  useCase: 'GMC_ADS',
  connectionCode: 'GOOGLE_MERCHANT',
  routeBase: '/enrichments/google',
  productSelectionRoute: '/enrichments/google/product-selection',
  contentEnrichmentBase: '/enrichments/google/content-enrichment',
  i18nKeyPrefix: 'googleProductSelection',
  // Backend (workflow-service `CurationScopeFilters`): "Missing required product scope
  // filters for GOOGLE_MERCHANT: target_country, content_language, channel, availability".
  requiredFilters: [COUNTRY_FILTER_KEYS, LANGUAGE_FILTER_KEYS, ['channel'], ['availability']],
};
